import CartItem from '@/components/sales/CartItem';
import DiscountCalculator from '@/components/sales/DiscountCalculator';
import ItemCalculator from '@/components/sales/ItemCalculator';
import SalesHeader from '@/components/sales/SalesHeader';
import SaveSaleBtn from '@/components/sales/SaveSaleBtn';
import SelectCategory from '@/components/sales/SelectCategory';
import useTicket from '@/hooks/sales/useTicketAttaches';
import {
  selectCurrency,
  selectSalesItemsLayout,
  setSalesItemsLayout,
} from '@/redux/config/configSlice';
import {
  selectActiveTicket,
  selectCategories,
  selectShift,
  updateTicket,
} from '@/redux/data/dataSlice';
import { DiscountType, ItemType, ModifierOptionType } from '@/redux/data/dataTypes';
import toaster from '@/toaster';
import { getTicketTotal } from '@/utils/ticket';
import {
  AppstoreOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { Badge, Button, Card, Modal, Tabs, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DiscountListWithWrapper from '../../components/discount/DiscountListWithWrapper';
import ItemsList from '../../components/items/ItemsList';
import ModifierListWithWrapper from '../../components/modifiers/ModifierListWithWrapper';
import ChargeBtn from '../../components/sales/ChargeBtn';
import { LayoutType } from '../../components/sales/LayoutToggle';
import SendToKitchenBtn from '../../components/sales/SendToKitchenBtn';
import OpenShiftLanding from '../../components/shift/OpenShiftLanding';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

const { Text, Title } = Typography;

const Sales = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const shift = useAppSelector(selectShift);
  const [activeTab, setActiveTab] = useState('ITEMS');
  const [itemCalculator, setItemCalculator] = useState<ItemType | null>(null);
  const [discountCalculator, setDiscountCalculator] = useState<DiscountType | null>(null);
  const activeTicket = useAppSelector(selectActiveTicket);
  const { addItem, addDiscount, addModifierOptions } = useTicket();
  const salesItemsLayout = useAppSelector(selectSalesItemsLayout);
  const currency = useAppSelector(selectCurrency);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const categories = useAppSelector(selectCategories);

  const ticketHasCategory = useMemo(() => {
    return Boolean(activeTicket && activeTicket?.category);
  }, [activeTicket]);
  const isDineIn = useMemo(() => {
    return activeTicket?.category === 'IN';
  }, [activeTicket]);

  const handleItemPress = useCallback(
    (item: ItemType) => {
      if (!item.price || item.sellingType === 'WEIGHT') {
        return setItemCalculator(item);
      }
      addItem(item);
    },
    [addItem]
  );

  const handleSubmitItemCalculate = useCallback(
    (item: ItemType) => {
      setItemCalculator(null);
      addItem(item);
    },
    [addItem]
  );

  const handleSubmitDiscountCalculate = useCallback(
    (discount: DiscountType) => {
      setDiscountCalculator(null);
      addDiscount(discount).catch((err) => {
        toaster.error(t(err));
      });
    },
    [addDiscount, t]
  );

  const handleDiscountPress = useCallback(
    (discount: DiscountType) => {
      if (!discount.value) return setDiscountCalculator(discount);
      addDiscount(discount).catch((err) => {
        toaster.error(t(err));
      });
    },
    [addDiscount, t]
  );

  const handleModifierOptionsSelect = useCallback(
    (options: ModifierOptionType[]) => {
      addModifierOptions(options);
    },
    [addModifierOptions]
  );

  const handleChargePress = useCallback(
    (id: string) => {
      navigate(`/ticket/${id}`);
    },
    [navigate]
  );

  const handleLayoutChange = useCallback(
    (layout: LayoutType) => {
      dispatch(setSalesItemsLayout(layout));
    },
    [dispatch]
  );

  const handleClearSale = useCallback(() => {
    if (!activeTicket) return;
    const newTicket = structuredClone(activeTicket);
    newTicket.items = [];
    newTicket.discounts = [];
    newTicket.modifierOptions = [];
    newTicket.createdAt = new Date().toISOString();
    dispatch(updateTicket(newTicket));
  }, [activeTicket, dispatch]);

  const total = useMemo(() => {
    const ticketTotal = getTicketTotal(activeTicket);
    return typeof ticketTotal === 'string' ? parseFloat(ticketTotal) : ticketTotal || 0;
  }, [activeTicket]);

  const itemsCount = useMemo(() => {
    return activeTicket?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  }, [activeTicket]);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const tabItems = useMemo(
    () => [
      {
        key: 'ITEMS',
        label: (
          <div className="flex items-center gap-2 px-2">
            <ShoppingCartOutlined />
            <span>{t('common:items')}</span>
          </div>
        ),
      },
      {
        key: 'DISCOUNTS',
        label: (
          <div className="flex items-center gap-2 px-2">
            <TagsOutlined />
            <span>{t('common:discounts')}</span>
          </div>
        ),
      },
      {
        key: 'MODIFIERS',
        label: (
          <div className="flex items-center gap-2 px-2">
            <AppstoreOutlined />
            <span>{t('common:modifiers')}</span>
          </div>
        ),
      },
    ],
    [t]
  );

  const renderList = useCallback(() => {
    switch (activeTab) {
      case 'ITEMS':
        return (
          <div className="space-y-2">
            {/* Enhanced Compact Categories */}
            <div className="bg-white rounded-lg border border-gray-200 p-2.5 mb-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <Text className="text-xs font-medium text-gray-700">{t('common:categories')}</Text>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                  <span className="text-[10px] text-gray-500">{categories?.length || 0}</span>
                </div>
              </div>

              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                <Button
                  type={selectedCategory === null ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory(null)}
                  className="flex-shrink-0 h-7 px-2 text-xs rounded-md font-medium whitespace-nowrap"
                  size="small"
                >
                  {t('common:all')}
                </Button>
                {categories?.map((category) => {
                  const isSelected = selectedCategory === category.ID;
                  const boxShadow = isSelected ? `0 0 0 2px ${category.color}20` : 'none';

                  return (
                    <Button
                      key={category.ID}
                      type={isSelected ? 'primary' : 'default'}
                      onClick={() => setSelectedCategory(category.ID)}
                      className="flex-shrink-0 h-7 px-2 text-xs rounded-md font-medium whitespace-nowrap transition-all duration-200 hover:scale-105"
                      size="small"
                      style={{
                        borderColor: category.color,
                        color: isSelected ? '#fff' : category.color,
                        backgroundColor: isSelected ? category.color : 'transparent',
                        boxShadow,
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="truncate max-w-[80px]">
                          {category.name.length > 10
                            ? `${category.name.substring(0, 10)}...`
                            : category.name}
                        </span>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>

            <ItemsList
              onItemPress={handleItemPress}
              layout={salesItemsLayout}
              searchTxt={searchQuery}
              selectedCategories={selectedCategory ? [selectedCategory] : []}
            />
          </div>
        );
      case 'DISCOUNTS':
        return (
          <Card className="shadow-sm border-0">
            <DiscountListWithWrapper onItemPress={handleDiscountPress} layout="compact" />
          </Card>
        );
      case 'MODIFIERS':
        return (
          <Card className="shadow-sm border-0">
            <ModifierListWithWrapper onOptionsSelect={handleModifierOptionsSelect} />
          </Card>
        );
      default:
        return null;
    }
  }, [
    activeTab,
    handleDiscountPress,
    handleItemPress,
    handleModifierOptionsSelect,
    salesItemsLayout,
    searchQuery,
    selectedCategory,
    categories,
    t,
  ]);

  if (itemCalculator) {
    return (
      <ItemCalculator
        item={itemCalculator}
        onClose={() => setItemCalculator(null)}
        onSubmit={handleSubmitItemCalculate}
      />
    );
  }

  if (discountCalculator) {
    return (
      <DiscountCalculator
        discount={discountCalculator}
        onClose={() => setDiscountCalculator(null)}
        onSubmit={handleSubmitDiscountCalculate}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <SalesHeader
        searchQuery={searchQuery}
        activeTab={activeTab}
        handleLayoutChange={handleLayoutChange}
        handleSearchQueryChange={handleSearchQueryChange}
      />

      <div className="flex-1 flex overflow-hidden">
        {shift && ticketHasCategory && (
          <>
            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              <div className="p-1.5 lg:p-2">
                <div className="mb-1 lg:mb-1.5">
                  <Tabs
                    items={tabItems}
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    size="small"
                    className="odoo-tabs"
                  />
                </div>

                <div className="flex-1 overflow-auto min-h-0">{renderList()}</div>
              </div>
            </div>

            {/* Enhanced Cart/Ticket Area */}
            <div className="w-full lg:w-[300px] xl:w-[320px] bg-white border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col shadow-lg max-h-[calc(100vh-40px)] overflow-auto">
              {/* Cart Header */}
              <div className="p-2 lg:p-2.5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <Title level={4} className="!mb-0 text-gray-800 text-sm lg:text-base">
                    {t('common:current_sale')}
                  </Title>
                  <div className="flex items-center gap-1.5">
                    <Badge
                      count={itemsCount}
                      className="animate-pulse"
                      style={{
                        backgroundColor: itemsCount > 0 ? '#1890ff' : '#d9d9d9',
                        fontSize: '10px',
                        fontWeight: 'bold',
                      }}
                    />
                    <ShoppingCartOutlined className="text-sm lg:text-base text-gray-600" />
                  </div>
                </div>

                <div className="bg-white rounded-lg p-1.5 lg:p-2 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <Text className="text-gray-600 text-[10px] lg:text-xs">
                      {t('common:total')}
                    </Text>
                    <Text className="text-base lg:text-lg font-bold text-[#17527B]">
                      {total.toFixed(2)} {currency}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-auto min-h-0 p-2 lg:p-2.5">
                {activeTicket?.items?.length ? (
                  <div className="space-y-1.5">
                    {activeTicket.items.map((item) => (
                      <CartItem key={item.ID} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-4 lg:py-6">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                      <ShoppingCartOutlined className="text-xl lg:text-2xl text-gray-400" />
                    </div>
                    <Text type="secondary" className="text-xs lg:text-sm mb-1">
                      {t('common:no_items_added')}
                    </Text>
                    <Text type="secondary" className="text-[9px] lg:text-[10px]">
                      {t('common:start_adding_items')}
                    </Text>
                  </div>
                )}
              </div>

              {/* Cart Actions */}
              <div className="p-2 lg:p-2.5 border-t border-gray-200 bg-white flex-shrink-0 space-y-1.5 lg:space-y-2">
                {activeTicket?.items && activeTicket?.items?.length > 0 && (
                  <>
                    {isDineIn ? (
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <ChargeBtn onPress={handleChargePress} />
                        </div>
                        <SendToKitchenBtn />
                      </div>
                    ) : (
                      <ChargeBtn onPress={handleChargePress} />
                    )}

                    <SaveSaleBtn />

                    <Button
                      danger
                      block
                      size="large"
                      icon={<DeleteOutlined className="text-xs" />}
                      className="h-8 lg:h-9 rounded-lg font-medium text-[10px] lg:text-xs"
                      onClick={() => {
                        Modal.confirm({
                          title: t('common:clear_sale_confirmation'),
                          content: t('common:clear_sale_confirmation_desc'),
                          okText: t('common:clear'),
                          okButtonProps: { danger: true },
                          onOk: handleClearSale,
                          centered: true,
                        });
                      }}
                    >
                      {t('common:clear_sale')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
        {shift && !ticketHasCategory && (
          <div className="flex-1 p-6">
            <SelectCategory />
          </div>
        )}
        {!shift && (
          <div className="flex-1 p-6">
            <OpenShiftLanding />
          </div>
        )}
      </div>
    </div>
  );
};

export default Sales;
